/**
 * Splitting one comparison section's table across PDF pages.
 *
 * html2pdf cannot break a table row, and the comparison PDF's answer for a
 * section taller than a page used to be to let the table FLOW: page-break
 * behavior of `auto`, and whatever landed past the boundary continued on the
 * next page with no heading and no column header over it. A reader meeting the
 * second half of Liver Function at the top of a page has no way to know what
 * they are looking at.
 *
 * So a long section is pre-paginated here instead, the same way the notes table
 * and Hormone Balance already are: each chunk is one page's worth of rows,
 * rendered as its own table with its own column header, under a heading that
 * repeats as "Liver Function (cont.)".
 *
 * This module is the arithmetic alone, so it can be tested without a browser.
 * The rows it counts are the two kinds a section table prints, in the order
 * `comparisonTable` prints them: the charted markers first, then the text
 * results.
 */

export type ComparisonRowChunk = {
  /** Charted-marker fields for this page, as slice bounds. */
  fieldStart: number;
  fieldEnd: number;
  /** Text results for this page, as slice bounds. */
  textStart: number;
  textEnd: number;
};

/**
 * A PAGE, AND THE TWO ROW HEIGHTS THAT FILL IT, in units where a one-line row
 * costs 100.
 *
 * SOLVED AGAINST TWO PRINTED PAGES of Jana's Urinalysis rather than derived,
 * because deriving it went wrong twice — once from a comment elsewhere in the
 * codebase, once from a flat cost for every text row. The two pages pin it
 * between them:
 *
 *   page one   15 one-line rows + 5 two-line rows fit; a 6th two-line did not
 *   page two    5 one-line rows + 11 two-line rows fit; a 12th did not
 *
 * A page of 2400 units, a one-line row at 100 and each extra line at 67,
 * reproduces both exactly: 2335 and 2337 spent, and in each case the next row
 * takes it past 2400.
 *
 * The ceiling for a section of nothing but one-line rows is 24, which sits
 * comfortably over the 22-row charted pages that print correctly today.
 *
 * These are the knobs, and the instruction for turning them has not changed:
 * print a report and count. A sliced row means the page is too generous; space
 * at the foot of a page means the opposite.
 */
const PAGE_BUDGET = 2400;
const ROW_COST = 100;
const EXTRA_LINE_COST = 67;

/** One text row: a line, plus each line it wraps onto. */
const costOfTextRow = (lines: number) =>
  ROW_COST + EXTRA_LINE_COST * Math.max(0, (Math.floor(lines) || 1) - 1);

/**
 * How many rows of charted markers fit on one page.
 *
 * Derived rather than set: a charted result is a number five or six characters
 * wide, so it never wraps and every charted row is a one-line row.
 */
export const COMPARISON_ROWS_PER_PDF_PAGE = Math.floor(PAGE_BUDGET / ROW_COST);

/** A page, and one row of it, for callers that place whole sections. */
export const COMPARISON_PAGE_BUDGET = PAGE_BUDGET;
export const COMPARISON_ROW_COST = ROW_COST;

/**
 * What a SECOND section costs the page it joins: its heading, the gap above it,
 * and its own column header — which is two lines deep, because the dates and
 * the range labels are stacked.
 *
 * The budget above was solved on pages carrying ONE section, so it already
 * holds one of these. Nothing charged for the second, and the rule that packs a
 * short section and a tall one onto one page read a page as holding 24 rows
 * when two sections' worth of chrome leaves room for 19.
 *
 * MEASURED off the page that reported it: Blood Clotting & Flow (11 rows) and
 * Liver Function (12) were packed together, 19 of the 23 rows printed and the
 * 20th did not — so the chrome is worth more than 400 and no more than 500. The
 * conservative end, because what being wrong by a row costs here is four liver
 * results at the top of the next page with no heading over them, and what being
 * right by a row too early costs is white space. Protein Status under Immune
 * Activity, 19 rows and the pairing this rule was written for, still fits at
 * exactly 2,400.
 */
export const COMPARISON_SECTION_BLOCK_COST = 500;

/**
 * What one whole section costs a page.
 *
 * The section-placement rules next door (which section opens a fresh page, and
 * whether a short one can lead a page a tall one then flows onto) used to count
 * ROWS while this module counted LINES, so the same urinalysis was sixteen rows
 * to one of them and a page and a half to the other. Sections were packed onto
 * pages they did not fit, and what printed was the tail of a table at the top
 * of a page with no heading over it.
 *
 * The Notes cell spans the section's rows rather than adding to them, so the
 * table is as tall as the TALLER of the two columns — which is why a five-row
 * section with a thirty-line note is a page of print.
 */
export function comparisonSectionCost(
  fieldCount: number,
  textRowLines: number[] = [],
  noteRows: number = 0,
): number {
  const fields = Math.max(0, Math.floor(fieldCount) || 0);
  const rowsCost =
    fields * ROW_COST
    + textRowLines.reduce((total, lines) => total + costOfTextRow(lines), 0);
  const noteCost = Math.max(0, Math.floor(noteRows) || 0) * ROW_COST;
  return Math.max(rowsCost, noteCost);
}

/**
 * How many characters of a text result fit on one line of its column.
 *
 * The result column is sized for a number, so the words the lab wrote wrap in
 * it. Counted off the printed export: "Negative" (8) holds one line, while
 * "Absent Risk" (11), "Positive ++" (11), "Not observed" (12), "Scant amount"
 * (12), "Slightly cloudy" (15) and "Moderate amount" (15) all take two.
 */
export const COMPARISON_TEXT_CHARS_PER_LINE = 10;

/**
 * How many lines a text result takes in that column.
 *
 * Wrapped at WORD boundaries, the way the browser does it: "Positive ++" costs
 * two lines and "5 - 10" costs one, and a plain character count over the line
 * width gets both of those wrong.
 */
export function comparisonTextRowLines(
  text: string | undefined,
  charsPerLine: number = COMPARISON_TEXT_CHARS_PER_LINE,
): number {
  const words = String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const width = Math.max(1, Math.floor(charsPerLine) || 1);
  if (!words.length) {
    return 1;
  }
  let lines = 1;
  let onThisLine = 0;
  words.forEach(word => {
    const needed = onThisLine ? onThisLine + 1 + word.length : word.length;
    if (onThisLine && needed > width) {
      lines += 1;
      onThisLine = word.length;
      return;
    }
    onThisLine = needed;
  });
  return lines;
}

/**
 * The pages one section's rows divide into.
 *
 * `textRowLines` is one entry per text result, holding the number of lines that
 * result takes — see comparisonTextRowLines. A count alone will not do: the
 * same 16 text rows fill a page when most of them wrap and leave a quarter of
 * it empty when they do not, which is how the first split of Jana's urinalysis
 * came out wrong in both directions at once.
 *
 * Always at least one chunk, even for a section with no rows at all: a section
 * can be nothing but its note, and returning no chunks would drop it from the
 * PDF rather than print it empty.
 */
export function packComparisonSectionRows(
  fieldCount: number,
  textRowLines: number[] = [],
): ComparisonRowChunk[] {
  const fields = Math.max(0, Math.floor(fieldCount) || 0);
  const texts = textRowLines.length;
  const total = fields + texts;

  if (total === 0) {
    return [{ fieldStart: 0, fieldEnd: 0, textStart: 0, textEnd: 0 }];
  }

  const costAt = (index: number) =>
    index < fields ? ROW_COST : costOfTextRow(textRowLines[index - fields]);

  const chunks: ComparisonRowChunk[] = [];
  let start = 0;
  while (start < total) {
    let spent = 0;
    let end = start;
    // `end > start` forces at least one row onto every page, so a row taller
    // than a whole page cannot spin this loop forever.
    while (end < total) {
      const cost = costAt(end);
      if (spent + cost > PAGE_BUDGET && end > start) {
        break;
      }
      spent += cost;
      end += 1;
    }
    chunks.push({
      // The window's overlap with the fields, then with the text results that
      // follow them. Either can be empty: a page can be all charted markers, a
      // mix, or — on a urinalysis — nothing but words.
      fieldStart: Math.min(start, fields),
      fieldEnd: Math.min(end, fields),
      textStart: Math.max(0, start - fields),
      textEnd: Math.max(0, end - fields),
    });
    start = end;
  }
  return chunks;
}

/** True when a section has more rows than one page can hold. */
export function comparisonSectionNeedsChunking(
  fieldCount: number,
  textRowLines: number[] = [],
): boolean {
  // The note is left out on purpose: it spans the rows rather than pushing them
  // down, so it cannot be fixed by cutting the section into more pages. What a
  // note longer than its page costs is its own overflow — see the note in
  // comparisonSectionCost.
  return comparisonSectionCost(fieldCount, textRowLines) > PAGE_BUDGET;
}
